import React, { useState, useEffect } from 'react';
import {
  FileText,
  Download,
  ChevronDown,
  ChevronRight,
  Target,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  Calendar,
  Award,
  Zap,
  RefreshCw
} from 'lucide-react';
import { reportingApi } from '../../reporting/api';

interface JourneyAlignedReportProps {
  reportId?: string;
  format?: 'pdf' | 'excel' | 'csv' | 'html';
}

interface ReportData {
  complianceAssertion: {
    framework: string;
    period: string;
    assertion: string;
    currentStage: string;
    lastAssessmentDate?: string;
  };
  journeyStage: {
    stage: string;
    label: string;
    description: string;
    color: string;
    progressPercentage: number;
  };
  evidenceCoverage: number;
  keyGaps: string[];
  pathToNextStage: string[];
  frameworkCompliance: Array<{
    framework: string;
    complianceRate: number;
    stage: string;
    evidenceCoverage: number;
  }>;
}

const JourneyAlignedReport: React.FC<JourneyAlignedReportProps> = ({
  reportId = "1",
  format = "pdf"
}) => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState<string[]>(['assertion', 'journey-stage', 'evidence-gaps']);

  useEffect(() => {
    loadReportData();
  }, [reportId]);

  const loadReportData = async () => {
    try {
      setLoading(true);
      const response = await reportingApi.getJourneyAlignedReport(reportId!);
      if (response.success) {
        setReportData(response.data);
      }
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'initial': return 'from-slate-500 to-slate-600';
      case 'in_progress': return 'from-blue-500 to-blue-600';
      case 'partially_compliant': return 'from-amber-500 to-orange-600';
      case 'substantially_compliant': return 'from-emerald-500 to-green-600';
      case 'audit_ready': return 'from-green-500 to-green-700';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const downloadReport = async () => {
    try {
      const response = await reportingApi.generateJourneyAlignedReport(reportId!, format!);
      if (response.success) {
        // Create download link
        const downloadUrl = response.data.downloadUrl;

        // Create temporary link and trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `compliance-journey-report.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Failed to download report:', error);
      alert('Failed to download report. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="text-slate-600 animate-spin" size={48} />
          <p className="text-slate-600 font-medium">Loading compliance journey report...</p>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="p-8 bg-white rounded-2xl shadow-sm border border-slate-200 max-w-md">
            <FileText className="text-slate-400 mb-4" size={64} />
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Report Not Found</h1>
            <p className="text-slate-600">Unable to load the requested compliance journey report.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
          <div className="bg-slate-700 px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <FileText className="text-white" size={32} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Compliance Journey Report</h1>
                  <p className="text-slate-100 mt-1">Journey-aligned compliance assessment and progress tracking</p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <div className="text-sm text-slate-300">Report ID</div>
                  <div className="text-lg font-semibold text-white">{reportId}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-slate-300">Format</div>
                  <div className="text-lg font-semibold text-white">{format.toUpperCase()}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Bar */}
          <div className="px-8 py-4 bg-slate-50 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <select
                  value={format}
                  onChange={(e) => window.location.href = `/journey-report/${reportId}?format=${e.target.value}`}
                  className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 bg-white"
                >
                  <option value="pdf">PDF Report</option>
                  <option value="excel">Excel Spreadsheet</option>
                  <option value="csv">CSV Data</option>
                  <option value="html">HTML Document</option>
                </select>
              </div>

              <button
                onClick={downloadReport}
                className="inline-flex items-center gap-2 px-6 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all"
              >
                <Download size={18} />
                Download Report
              </button>
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-6">
          {/* Compliance Assertion Section */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div
              className="px-6 py-4 bg-slate-50 border-b border-blue-200 cursor-pointer flex items-center justify-between hover:bg-gradient-to-r transition-all"
              onClick={() => toggleSection('assertion')}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Target className="text-blue-600" size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-blue-900">1. Compliance Assertion</h2>
                  <p className="text-sm text-blue-700">Framework compliance statement and current status</p>
                </div>
              </div>
              {expandedSections.includes('assertion') ?
                <ChevronDown className="text-blue-500" size={24} /> :
                <ChevronRight className="text-blue-500" size={24} />
              }
            </div>

            {expandedSections.includes('assertion') && (
              <div className="p-8">
                <div className="bg-slate-50 border border-blue-200 rounded-xl p-8 mb-6">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-4">
                      <Award className="text-blue-600" size={16} />
                      <span className="text-blue-800 font-medium">COMPLIANCE ASSERTION</span>
                    </div>
                    <h3 className="text-xl font-bold text-blue-900 mb-2">
                      "{reportData.complianceAssertion.assertion}"
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white rounded-xl p-6 border border-blue-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <FileText className="text-blue-600" size={20} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">Framework</h4>
                          <p className="text-sm text-slate-600">Compliance standard</p>
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-blue-600">{reportData.complianceAssertion.framework}</div>
                    </div>

                    <div className="bg-white rounded-xl p-6 border border-blue-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Calendar className="text-blue-600" size={20} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">Period</h4>
                          <p className="text-sm text-slate-600">Assessment period</p>
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-blue-600">{reportData.complianceAssertion.period}</div>
                    </div>

                    <div className="bg-white rounded-xl p-6 border border-blue-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <TrendingUp className="text-blue-600" size={20} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">Current Stage</h4>
                          <p className="text-sm text-slate-600">Compliance level</p>
                        </div>
                      </div>
                      <div className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full text-white bg-gradient-to-r ${getStageColor(reportData.complianceAssertion.currentStage)}`}>
                        {reportData.complianceAssertion.currentStage.replace('_', ' ').toUpperCase()}
                      </div>
                    </div>
                  </div>

                  {reportData.complianceAssertion.lastAssessmentDate && (
                    <div className="mt-6 text-center">
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full">
                        <Calendar className="text-slate-600" size={16} />
                        <span className="text-slate-700 font-medium">Last Assessment: {formatDate(reportData.complianceAssertion.lastAssessmentDate)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Journey Stage Section */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div
              className="px-6 py-4 border-b border-slate-200 cursor-pointer flex items-center justify-between hover:bg-slate-50 transition-all"
              onClick={() => toggleSection('journey-stage')}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <TrendingUp className="text-emerald-600" size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">2. Current Journey Stage</h2>
                  <p className="text-sm text-slate-600">Progress tracking and next steps</p>
                </div>
              </div>
              {expandedSections.includes('journey-stage') ?
                <ChevronDown className="text-slate-500" size={24} /> :
                <ChevronRight className="text-slate-500" size={24} />
              }
            </div>

            {expandedSections.includes('journey-stage') && (
              <div className="p-8">
                <div className="flex items-center mb-8">
                  <div className={`w-20 h-20 rounded-full bg-gradient-to-r ${getStageColor(reportData.journeyStage.stage)} flex items-center justify-center mr-6 shadow-lg`}>
                    <span className="text-2xl font-bold text-white">{reportData.journeyStage.progressPercentage}%</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">{reportData.journeyStage.label}</h3>
                    <p className="text-slate-600 text-lg">{reportData.journeyStage.description}</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-lg font-semibold text-slate-900">Journey Progress</span>
                    <span className="text-lg font-bold text-slate-900">{reportData.journeyStage.progressPercentage}% Complete</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-4 shadow-inner">
                    <div
                      className={`h-4 rounded-full bg-gradient-to-r ${getStageColor(reportData.journeyStage.stage)} shadow-sm`}
                      style={{ width: `${reportData.journeyStage.progressPercentage}%` }}
                    ></div>
                  </div>
                </div>

                {/* Path to Next Stage */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="text-emerald-600" size={20} />
                    <h4 className="text-lg font-semibold text-slate-900">Path to Next Stage</h4>
                  </div>
                  <div className="space-y-3">
                    {reportData.pathToNextStage.map((path, index) => (
                      <div key={index} className="flex items-start gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                        <div className="p-2 bg-emerald-100 rounded-lg mt-0.5">
                          <CheckCircle className="text-emerald-600" size={16} />
                        </div>
                        <span className="text-slate-900 font-medium">{path}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Evidence & Gaps Section */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div
              className="px-6 py-4 border-b border-slate-200 cursor-pointer flex items-center justify-between hover:bg-slate-50 transition-all"
              onClick={() => toggleSection('evidence-gaps')}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BarChart3 className="text-purple-600" size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">3. Evidence & Gaps Analysis</h2>
                  <p className="text-sm text-slate-600">Coverage assessment and framework compliance</p>
                </div>
              </div>
              {expandedSections.includes('evidence-gaps') ?
                <ChevronDown className="text-slate-500" size={24} /> :
                <ChevronRight className="text-slate-500" size={24} />
              }
            </div>

            {expandedSections.includes('evidence-gaps') && (
              <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                  {/* Evidence Coverage */}
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <CheckCircle className="text-green-600" size={20} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">Evidence Coverage</h3>
                        <p className="text-sm text-slate-600">Documentation completeness</p>
                      </div>
                    </div>

                    <div className="flex items-center mb-4">
                      <div className="flex-1 bg-slate-200 rounded-full h-4 mr-4">
                        <div
                          className="bg-green-600 h-4 rounded-full shadow-sm"
                          style={{ width: `${reportData.evidenceCoverage}%` }}
                        ></div>
                      </div>
                      <span className="text-2xl font-bold text-slate-900">
                        {reportData.evidenceCoverage}%
                      </span>
                    </div>

                    <div className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                      reportData.evidenceCoverage >= 95
                        ? 'text-emerald-800 bg-emerald-100'
                        : reportData.evidenceCoverage >= 75
                        ? 'text-amber-800 bg-amber-100'
                        : 'text-red-800 bg-red-100'
                    }`}>
                      {reportData.evidenceCoverage >= 95
                        ? 'Excellent - Audit Ready'
                        : reportData.evidenceCoverage >= 75
                        ? 'Good - Minor Gaps'
                        : 'Needs Improvement'
                      }
                    </div>
                  </div>

                  {/* Key Gaps */}
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <AlertTriangle className="text-red-600" size={20} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">Key Gaps Identified</h3>
                        <p className="text-sm text-slate-600">Areas requiring attention</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {reportData.keyGaps.length === 0 ? (
                        <div className="text-center py-8">
                          <CheckCircle className="text-emerald-400 mx-auto mb-2" size={32} />
                          <p className="text-slate-600">No critical gaps identified</p>
                        </div>
                      ) : (
                        reportData.keyGaps.map((gap, index) => (
                          <div key={index} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                            <AlertTriangle className="text-red-600 mt-0.5 flex-shrink-0" size={16} />
                            <span className="text-red-900 font-medium">{gap}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Framework Compliance Table */}
                <div>
                  <div className="flex items-center gap-2 mb-6">
                    <BarChart3 className="text-slate-600" size={20} />
                    <h3 className="text-lg font-semibold text-slate-900">Framework Compliance Summary</h3>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Framework</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Compliance Rate</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Stage</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Evidence Coverage</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {reportData.frameworkCompliance.map((framework, index) => (
                          <tr key={index} className="hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium text-slate-900">{framework.framework}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-left">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-slate-200 rounded-full h-2">
                                  <div
                                    className="bg-blue-600 h-2 rounded-full"
                                    style={{ width: `${framework.complianceRate}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm font-semibold text-slate-900">{framework.complianceRate}%</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-left">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full text-white bg-gradient-to-r ${getStageColor(framework.stage)}`}>
                                {framework.stage.replace('_', ' ').toUpperCase()}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-slate-900">{framework.evidenceCoverage}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-center gap-2 text-slate-500 mb-2">
              <Calendar size={16} />
              <span className="text-sm">Report generated on {new Date().toLocaleDateString()}</span>
            </div>
            <p className="text-slate-600 text-sm">
              For questions about this compliance journey report, please contact your compliance administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JourneyAlignedReport;